<template>
  <n-modal
    v-model:show="modalVisible"
    preset="dialog"
    :mask-closable="false"
    :close-on-esc="false"
    :closable="false"
    :showIcon="false"
    class="bind-mobile-modal"
    content-class="bind-mobile-modal__content"
  >
    <div class="bind-mobile-modal__header">
      <h3 class="bind-mobile-modal__title">绑定手机号</h3>
      <n-button text class="bind-mobile-modal__skip" @click="handleSkip">
        跳过>
      </n-button>
    </div>

    <p class="bind-mobile-modal__intro">
      为了保障账号安全和后续通知触达，请先绑定本人手机号。
    </p>

    <HpForm ref="formRef" v-model="formData" :rules="rules" :form-items="formItems">
      <template #captchaCode="{ formData, formItem }">
        <customInput v-model="formData[formItem.path]" v-bind="formItem">
          <template #suffix>
            <img
              class="bind-mobile-modal__captcha"
              :src="captchaImage"
              alt="图形验证码"
              @click="fetchCaptcha"
            />
          </template>
        </customInput>
      </template>
      <template #smsCode="{ formData, formItem }">
        <customInput v-model="formData[formItem.path]" v-bind="formItem">
          <template #suffix>
            <CustomButton v-if="!active" type="primary" text @click="fetchSmsCode">
              获取验证码
            </CustomButton>
            <n-countdown
              v-else
              :duration="duration"
              :active="active"
              :on-finish="handleDuration"
            />
          </template>
        </customInput>
      </template>
    </HpForm>

    <CustomButton
      type="primary"
      round
      block
      size="large"
      class="bind-mobile-modal__submit"
      @click="handleSubmit"
    >
      确认绑定
    </CustomButton>
  </n-modal>
</template>

<script setup lang="ts">
import HpForm from '@/base-ui/hp-form'
import customInput from '@/base-ui/hp-form/src/components/customInput.vue'
import { useBaseBusinessStore } from '@/store/base/baseStore'
import useLoginStore from '@/store/login/login'
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'

const loginStore = useLoginStore()
const baseStore = useBaseBusinessStore('app-api/system')
const formRef = ref()
const active = ref(false)
const duration = ref(60 * 100)
const captchaImage = ref('')
const formData = ref({
  mobile: '',
  captchaCode: '',
  captchaKey: '',
  smsCode: ''
})

const modalVisible = computed({
  get: () => loginStore.bindMobileModalVisible,
  set: (value) => {
    loginStore.bindMobileModalVisible = value
  }
})

const rules = {
  mobile: [
    {
      required: true,
      pattern: /^1[3-9]\d{9}$/,
      message: '手机号格式错误',
      trigger: ['input', 'blur']
    }
  ],
  captchaCode: [
    {
      required: true,
      message: '请输入图形验证码',
      trigger: ['input', 'blur']
    }
  ],
  smsCode: [
    {
      required: true,
      message: '请输入短信验证码',
      trigger: ['input', 'blur']
    }
  ]
}

const formItems = [
  {
    path: 'mobile',
    placeholder: '请输入手机号',
    type: 'input',
    prefixIcon: 'shouji',
    prefixIconSize: '22px',
    size: 'large',
    bordered: true
  },
  {
    path: 'captchaCode',
    placeholder: '请输入图形验证码',
    type: 'slot',
    slotName: 'captchaCode',
    prefixIcon: 'tupian',
    prefixIconSize: '20px',
    size: 'large',
    bordered: true
  },
  {
    path: 'smsCode',
    slotName: 'smsCode',
    placeholder: '请输入验证码',
    type: 'slot',
    prefixIcon: 'zhibao',
    prefixIconSize: '24px',
    size: 'large',
    bordered: true
  }
]

const resetForm = () => {
  formData.value = {
    mobile: loginStore.userInfo?.mobile || '',
    captchaCode: '',
    captchaKey: '',
    smsCode: ''
  }
  active.value = false
  duration.value = 60 * 100
}

const fetchCaptcha = async () => {
  const res = await baseStore.getRequest('captcha/image', undefined, {
    needAuth: false
  })

  if (res.code === 0) {
    captchaImage.value = res.data.image
    formData.value.captchaKey = res.data.captchaKey
    return
  }

  ElMessage.error(res.msg || '获取图形验证码失败')
}

const fetchSmsCode = async () => {
  if (!formData.value.mobile || !formData.value.captchaCode || !formData.value.captchaKey) {
    ElMessage.warning('请先填写手机号和图形验证码')
    return
  }

  active.value = true
  const res = await baseStore.postRequest(
    'auth/send-sms-code',
    {
      mobile: formData.value.mobile,
      captchaCode: formData.value.captchaCode,
      captchaKey: formData.value.captchaKey
    },
    {
      needAuth: false,
      showSuccessMessage: true,
      successMessage: '验证码已发送，请注意查收'
    }
  )

  if (res.code !== 0) {
    active.value = false
    duration.value = 60 * 100
    ElMessage.error(res.msg || '发送验证码失败')
    await fetchCaptcha()
  }
}

const handleDuration = () => {
  active.value = false
  duration.value = 60 * 100
}

const handleSkip = () => {
  loginStore.skipCurrentPostLoginReminder()
}

const handleSubmit = async () => {
  if (!formRef.value) return
  const isValid = await formRef.value.validate()
  if (!isValid) return

  const payload = {
    ...loginStore.userInfo,
    mobile: formData.value.mobile,
    smsCode: formData.value.smsCode
  }

  const res = await loginStore.runWithUserInfoRefresh(() =>
    baseStore.putRequest('user/profile/update', payload)
  )
  if (res.code !== 0) {
    ElMessage.error(res.msg || '绑定手机号失败')
    return
  }

  ElMessage.success('手机号绑定成功')
  loginStore.completeCurrentPostLoginReminder()
}

watch(
  modalVisible,
  async (value) => {
    if (!value) return
    resetForm()
    await fetchCaptcha()
  },
  { immediate: true }
)
</script>

<style lang="scss">
.bind-mobile-modal {
  width: 480px !important;
  padding: 0 30px 24px !important;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 20px 0;
  }

  &__title {
    flex: 1;
    margin: 0;
    text-align: center;
    font-size: 20px;
    color: #1e1e1e;
  }

  &__skip {
    color: #909399;
  }

  &__intro {
    margin: 0 0 20px;
    color: #606266;
    font-size: 14px;
    line-height: 1.8;
  }

  &__captcha {
    width: 100px;
    height: 36px;
    margin-left: 10px;
    cursor: pointer;
  }

  &__submit {
    margin-top: 16px;
  }
}
</style>
